import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MegaphoneIcon,
    PencilSquareIcon,
    TrashIcon,
    CalendarIcon,
    UserIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA ---
const initialAnnouncements = [
    {
        id: 1,
        title: 'Company-Wide Holiday Party on December 20th!',
        message: 'Join us for our annual holiday celebration! We will have food, drinks, and a special award ceremony to honor our top performers. All employees and their plus-ones are welcome. Please RSVP by December 10th.',
        author: 'HR Department',
        date: '2025-11-25',
    },
    {
        id: 2,
        title: 'New Health and Wellness Program Launch',
        message: 'We are excited to announce a new health and wellness program starting next month. This initiative includes gym membership discounts, a subscription to a meditation app, and free weekly yoga classes. More details will be shared in your email.',
        author: 'Management',
        date: '2025-11-20',
    },
    {
        id: 3,
        title: 'Project Alpha Deadline Extended',
        message: 'Due to unforeseen circumstances, the deadline for Project Alpha has been extended to November 30th. We appreciate your hard work and flexibility during this time.',
        author: 'Project Lead',
        date: '2025-11-15',
    },
];
// --- END: MOCK DATA ---

const Announcements = () => {
    const [announcements, setAnnouncements] = useState(initialAnnouncements);
    const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '' });
    
    // Simple state to simulate user role (e.g., HR or Admin)
    const isAdmin = true;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewAnnouncement(prevState => ({ ...prevState, [name]: value }));
    };

    const handlePostAnnouncement = (e) => {
        e.preventDefault();
        if (newAnnouncement.title && newAnnouncement.message) {
            const post = {
                id: Date.now(),
                title: newAnnouncement.title,
                message: newAnnouncement.message,
                author: 'HR Admin', // Hardcoded for this example
                date: moment().format('YYYY-MM-DD'),
            };
            setAnnouncements(prevAnnouncements => [post, ...prevAnnouncements]);
            setNewAnnouncement({ title: '', message: '' });
        }
    };

    const handleDeleteAnnouncement = (id) => {
        setAnnouncements(prevAnnouncements => prevAnnouncements.filter(ann => ann.id !== id));
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
        exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-8 text-gray-800">
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto mb-8"
            >
                <h1 className="text-4xl font-extrabold text-gray-900 flex items-center">
                    <MegaphoneIcon className="h-10 w-10 text-blue-600 mr-4" />
                    Announcements
                </h1>
                <p className="text-xl font-light text-gray-600 mt-2">
                    Stay up-to-date with important company news.
                </p>
            </motion.div>

            {/* Admin Post Announcement Form (Conditionally rendered) */}
            {isAdmin && (
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    className="bg-white p-6 rounded-3xl shadow-lg mb-8 max-w-7xl mx-auto"
                >
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                        <PencilSquareIcon className="h-6 w-6 text-gray-500 mr-2" />
                        Post a New Announcement
                    </h3>
                    <form onSubmit={handlePostAnnouncement} className="space-y-4">
                        <input
                            type="text"
                            name="title"
                            value={newAnnouncement.title}
                            onChange={handleInputChange}
                            placeholder="Announcement Title"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            required
                        />
                        <textarea
                            name="message"
                            value={newAnnouncement.message}
                            onChange={handleInputChange}
                            placeholder="Type your message here..."
                            rows="4"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            required
                        />
                        <button
                            type="submit"
                            className="w-full py-3 px-6 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                        >
                            Post Announcement
                        </button>
                    </form>
                </motion.div>
            )}

            {/* Announcements Feed */}
            <div className="max-w-7xl mx-auto space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Recent Announcements</h3>
                <AnimatePresence>
                    {announcements.length > 0 ? (
                        announcements.map(announcement => (
                            <motion.div
                                key={announcement.id}
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                layout
                                className="bg-white p-6 rounded-3xl shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center"
                            >
                                <div className="flex-1">
                                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                                        {announcement.title}
                                    </h4>
                                    <p className="text-gray-600 mb-4 md:mb-0">
                                        {announcement.message}
                                    </p>
                                    <div className="mt-4 text-xs text-gray-500 flex items-center space-x-4">
                                        <div className="flex items-center space-x-1">
                                            <UserIcon className="h-4 w-4" />
                                            <span>{announcement.author}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <CalendarIcon className="h-4 w-4" />
                                            <span>{moment(announcement.date).format('MMMM D, YYYY')}</span>
                                        </div>
                                    </div>
                                </div>
                                {isAdmin && (
                                    <button
                                        onClick={() => handleDeleteAnnouncement(announcement.id)}
                                        className="mt-4 md:mt-0 p-2 text-red-500 hover:text-red-700 transition-colors"
                                    >
                                        <TrashIcon className="h-6 w-6" />
                                    </button>
                                )}
                            </motion.div>
                        ))
                    ) : (
                        <div className="text-center text-gray-500 py-10">
                            No announcements have been posted yet.
                        </div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Announcements;